// src/components/projects/ProjectActions.tsx
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faEdit,
  faTrash,
  faComments,
  faFileUpload,
  faTasks,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { EntityType, UploadContexts } from '../../constants/globalConstants';
import { ProjectDTO } from './ProjectApi';

interface ProjectActionsProps {
  project: ProjectDTO;
  onDelete: (id: string) => void;
}

const ProjectActions: React.FC<ProjectActionsProps> = ({ project, onDelete }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const actionButtons = [
    {
      icon: faEye,
      label: 'View',
      className: 'bg-blue-600 hover:bg-blue-700',
      onClick: () => project.id && navigate(`/projects/${project.id}`),
    },
    {
      icon: faEdit,
      label: 'Edit',
      className: 'bg-yellow-500 hover:bg-yellow-700',
      onClick: () => project.id && navigate(`/projects/${project.id}/edit`),
    },
    {
      icon: faTrash,
      label: 'Delete',
      className: !!project.contractId
        ? 'bg-gray-400 cursor-not-allowed'
        : 'bg-red-600 hover:bg-red-700',
      disabled: !!project.contractId,
      onClick: () => !project.contractId && project.id && onDelete(project.id),
    },
    {
      icon: faFileUpload,
      label: 'Add Supporting Docs',
      className: 'bg-green-600 hover:bg-green-700',
      onClick: () =>
        project.id &&
        navigate('/uploads', {
          state: {
            context: UploadContexts.PROJECT_ATTACHMENTS,
            entityId: project.id,
            entityType: EntityType.PROJECT,
            metadata: {
              projectName: project.projectName,
              projectDescription: project.description,
              projectType: project.projectType,
              status: project.status,
            },
            title: 'Upload Supporting Documents for Project',
            description: 'Upload any supporting documents related to this project.',
            uploadType: 'PROJECT_SUPPORT_DOCS',
            maxFiles: 10,
            allowedFileTypes: [
              'application/pdf',
              'image/*',
              'application/msword',
              'video/*',
            ],
          },
        }),
    },
    {
      icon: faTasks,
      label: 'Project Tasks',
      className: 'bg-indigo-600 hover:bg-indigo-700',
      onClick: () =>
        project.id &&
        navigate('/selected-project-tasks', {
          state: {
            projectId: project.id,
            projectName: project.projectName,
            projectType: project.projectType,
            description: project.description,
            status: project.status,
          },
        }),
    },
    {
      icon: faComments,
      label: 'Add Comments',
      className: 'bg-blue-500 hover:bg-blue-600',
      onClick: () =>
        navigate('/comments', {
          state: {
            context: UploadContexts.PROJECT_ATTACHMENTS,
            entityId: project.id,
            entityType: EntityType.PROJECT,
            metadata: {
              projectName: project.projectName,
              projectDescription: project.description,
              projectType: project.projectType,
              status: project.status,
            },
            title: 'Comments for Project',
            description: 'Project Comments',
            uploadType: 'PROJECT_SUPPORT_DOCS',
            maxFiles: 10,
            allowedFileTypes: [
              'application/pdf',
              'image/*',
              'application/msword',
              'video/*',
            ],
          },
        }),
    },
  ];

  return (
    <div className="space-y-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full bg-gray-200 text-gray-700 px-2 py-1 rounded hover:bg-gray-300 flex items-center justify-center gap-2 text-xs font-medium"
        title={expanded ? 'Hide Actions' : 'Show Actions'}
      >
        <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
        {expanded ? 'Hide Actions' : 'Show Actions'}
      </button>

      {expanded ? (
        <div className="space-y-1 transition-all duration-300 ease-in-out">
          {actionButtons.map(({ icon, label, className, onClick, disabled }) => (
            <button
              key={label}
              className={`${className} text-white px-2 py-1 rounded w-full flex items-center justify-center gap-2`}
              onClick={onClick}
              disabled={disabled}
              title={label}
            >
              <FontAwesomeIcon icon={icon} />
              {label}
            </button>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-1 mt-1 transition-all duration-300 ease-in-out">
          {actionButtons.map(({ icon, label, className, onClick, disabled }) => (
            <button
              key={label}
              className={`${className} text-white p-2 rounded flex items-center justify-center`}
              onClick={onClick}
              disabled={disabled}
              title={label}
            >
              <FontAwesomeIcon icon={icon} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectActions;